import apiClient from './axios';
import type { KnowledgeAudience, KnowledgeCategory } from './knowledge-gaps';

export type { KnowledgeAudience, KnowledgeCategory };

export interface KnowledgeDocument {
  id: string;
  title: string;
  category: KnowledgeCategory;
  audience: KnowledgeAudience;
  source: string | null;
  content: string;
  isActive: boolean;
  /**
   * How many embedded chunks the document produced.
   *
   * Zero means it is stored but unretrievable — the embedding pass failed,
   * and nothing else about the row would say so. Worth surfacing rather than
   * leaving somebody to wonder why a document they wrote never comes back.
   */
  chunkCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeDocumentPage {
  items: KnowledgeDocument[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const knowledgeDocumentsApi = {
  async list(params: {
    page?: number;
    limit?: number;
    audience?: KnowledgeAudience;
    category?: KnowledgeCategory;
    search?: string;
  }): Promise<KnowledgeDocumentPage> {
    const res = await apiClient.get('/admin/knowledge-documents', { params });
    return res.data.data;
  },

  async create(payload: {
    title: string;
    category: KnowledgeCategory;
    audience: KnowledgeAudience;
    content: string;
    source?: string;
  }): Promise<KnowledgeDocument> {
    const res = await apiClient.post('/admin/knowledge-documents', payload);
    return res.data.data;
  },

  /** Editing the content re-embeds it server-side; editing anything else
   *  does not. Nothing to do differently from here. */
  async update(
    id: string,
    payload: Partial<{
      title: string;
      category: KnowledgeCategory;
      audience: KnowledgeAudience;
      content: string;
      isActive: boolean;
    }>,
  ): Promise<KnowledgeDocument> {
    const res = await apiClient.patch(`/admin/knowledge-documents/${id}`, payload);
    return res.data.data;
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/admin/knowledge-documents/${id}`);
  },
};
